import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import NextActivityCard from '../components/programme/NextActivityCard';
import axios from 'axios';
import { API_BASE } from '../config/api';
import {
  ArrowLeft,
  ArrowRight,
  Beaker,
  Brain,
  Send,
  Volume2,
  VolumeX,
  Loader2,
  CheckCircle,
  XCircle,
  Award,
  Target,
  MessageCircle,
  RefreshCw,
  ChevronRight,
  AlertTriangle,
  Sparkles,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { LabBadgeEarned } from '../components/labs/LabBadge';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import ContentFeedback from '../components/ContentFeedback';
const API = `${API_BASE}/api`;

export default function LearningLabPlayer() {
  const { labId } = useParams();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  
  const [lab, setLab] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [currentStep, setCurrentStep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  
  // Response state
  const [textResponse, setTextResponse] = useState('');
  const [selectedChoice, setSelectedChoice] = useState(null);
  
  // NAVI feedback state
  const [naviFeedback, setNaviFeedback] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [pendingNextStep, setPendingNextStep] = useState(null);
  
  // Completion state
  const [isCompleted, setIsCompleted] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [badge, setBadge] = useState(null);
  const [searchParams] = useSearchParams();
  const [passMark, setPassMark] = useState(50);
  const [developmentAreas, setDevelopmentAreas] = useState([]);
  
  const token = localStorage.getItem('token');

  // Generate fallback development areas if none exist but competency < 100%
  const displayDevelopmentAreas = useMemo(() => {
    if (developmentAreas?.length > 0) return developmentAreas;
    
    if (isCompleted && attempt?.competency_score < 100) {
      const score = attempt?.competency_score || 0;
      if (score < 70) {
        return [
          "Review the core compliance principles covered in this scenario",
          "Practice identifying the optimal response strategies in similar situations",
          "Focus on understanding the regulatory frameworks that apply to this context"
        ];
      } else if (score < 90) {
        return [
          "Consider the nuanced aspects of compliance decisions in edge cases",
          "Strengthen your documentation and reporting practices"
        ];
      } else {
        return [
          "Continue building expertise in specialized areas of compliance"
        ];
      }
    }
    return [];
  }, [developmentAreas, isCompleted, attempt?.competency_score]);

  useEffect(() => {
    loadLabAndStart();
  }, [labId]);

  const loadLabAndStart = async () => {
    setLoading(true);
    try {
      // Load lab details
      const labRes = await axios.get(`${API}/labs/${labId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLab(labRes.data);

      // Start or resume attempt
      const attemptRes = await axios.post(`${API}/labs/${labId}/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAttempt(attemptRes.data.attempt);
      
      // Check if the lab attempt is already completed
      if (attemptRes.data.is_completed) {
        setIsCompleted(true);
        setOutcome(attemptRes.data.outcome);
        axios.get(`${API}/labs/${labId}/my-badge`, { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => { setBadge(r.data.badge); setPassMark(r.data.pass_mark); }).catch(() => {});
        setDevelopmentAreas(attemptRes.data.development_areas || attemptRes.data.attempt.development_areas || []);
        return;
      }
      
      // Find current step
      const currentStepId = attemptRes.data.attempt.current_step_id;
      const step = attemptRes.data.current_step || 
        labRes.data.steps.find(s => s.step_id === currentStepId);
      setCurrentStep(step);
      
    } catch (error) {
      console.error('Failed to load lab:', error);
      toast.error('Failed to load learning lab');
    } finally {
      setLoading(false);
    }
  };

  const submitResponse = async () => {
    if (!currentStep) return;
    
    const isChoice = currentStep.step_type === 'choice';
    const isText = currentStep.step_type === 'text_response';
    
    if (isChoice && !selectedChoice) {
      toast.error('Please select a choice');
      return;
    }
    if (isText && !textResponse.trim()) {
      toast.error('Please enter your response');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/labs/${labId}/respond`, {
        attempt_id: attempt.attempt_id,
        step_id: currentStep.step_id,
        user_response: isText ? textResponse : '',
        response_type: isChoice ? 'choice' : 'text',
        selected_choice_id: isChoice ? selectedChoice : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Show NAVI feedback
      setNaviFeedback({
        feedback: response.data.navi_feedback,
        points: response.data.points_earned,
        competency: response.data.competency_score,
        totalPoints: response.data.total_points,
        overallCompetency: response.data.overall_competency
      });
      setShowFeedback(true);
      
      // Check completion
      if (response.data.is_completed) {
        setIsCompleted(true);
        setOutcome(response.data.outcome);
        setDevelopmentAreas(response.data.development_areas || []);
        
        // Update attempt with final stats
        setAttempt(prev => ({
          ...prev,
          total_points: response.data.total_points,
          competency_score: response.data.overall_competency
        }));
        setBadge(response.data.badge || null);
        if (response.data.pass_mark) setPassMark(response.data.pass_mark);
        if (response.data.badge?.newly_awarded) toast.success('You earned a Navigate badge!');
      } else {
        // Store next step for when user clicks Continue
        if (response.data.next_step) {
          setPendingNextStep(response.data.next_step);
        }
      }
      
    } catch (error) {
      console.error('Failed to submit response:', error);
      toast.error('Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  const continueToNext = () => {
    // Move to the next step that was stored when response was received
    if (pendingNextStep) {
      setCurrentStep(pendingNextStep);
      setPendingNextStep(null);
    }
    setShowFeedback(false);
    setNaviFeedback(null);
    setTextResponse('');
    setSelectedChoice(null);
  };

  const handleInformationContinue = async () => {
    // For information/checkpoint steps, just proceed to next
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/labs/${labId}/respond`, {
        attempt_id: attempt.attempt_id,
        step_id: currentStep.step_id,
        user_response: '',
        response_type: 'continue',
        selected_choice_id: null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.is_completed) {
        setIsCompleted(true);
        setOutcome(response.data.outcome);
      } else if (response.data.next_step) {
        setCurrentStep(response.data.next_step);
      }
    } catch (error) {
      console.error('Failed to continue:', error);
      toast.error('Failed to continue');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleAudio = () => {
    if (!audioRef.current) return;
    
    if (audioPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setAudioPlaying(!audioPlaying);
  };

  const restartLab = () => {
    setAttempt(null);
    setCurrentStep(null);
    setIsCompleted(false);
    setOutcome(null);
    setNaviFeedback(null);
    setShowFeedback(false);
    setTextResponse('');
    setSelectedChoice(null);
    loadLabAndStart();
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-navigate-gold mx-auto mb-4" />
            <p className="text-zinc-400">Loading scenario...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!lab || (!currentStep && !isCompleted)) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Lab Not Found</h2>
            <Button onClick={() => navigate('/labs')}>
              Back to Labs
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  // Completion Screen
  if (isCompleted) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-navigate-surface border border-zinc-800 rounded-2xl overflow-hidden">
            {/* Outcome Header */}
            <div className={`p-8 text-center ${
              outcome?.outcome_type === 'optimal' ? 'bg-gradient-to-br from-green-900/50 to-green-500/20' :
              outcome?.outcome_type === 'acceptable' ? 'bg-gradient-to-br from-amber-900/50 to-amber-500/20' :
              outcome?.outcome_type === 'poor' ? 'bg-gradient-to-br from-orange-900/50 to-orange-500/20' :
              'bg-gradient-to-br from-red-900/50 to-red-500/20'
            }`}>
              <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center bg-black/20">
                {outcome?.outcome_type === 'optimal' ? (
                  <Award className="h-10 w-10 text-green-400" />
                ) : outcome?.outcome_type === 'acceptable' ? (
                  <CheckCircle className="h-10 w-10 text-amber-400" />
                ) : (
                  <AlertTriangle className="h-10 w-10 text-orange-400" />
                )}
              </div>
              
              <h1 className="text-2xl font-bold text-white mb-2">
                {outcome?.title || 'Scenario Complete'}
              </h1>
              <p className="text-zinc-300 max-w-2xl mx-auto">
                {outcome?.description}
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 divide-x divide-zinc-800 border-b border-zinc-800">
              <div className="p-6 text-center">
                <p className="text-3xl font-bold text-white">{attempt?.total_points || 0}</p>
                <p className="text-sm text-zinc-400">Points Earned</p>
              </div>
              <div className="p-6 text-center">
                <p className="text-3xl font-bold text-navigate-gold">{Math.round(attempt?.competency_score || 0)}%</p>
                <p className="text-sm text-zinc-400">Competency Score</p>
              </div>
              <div className="p-6 text-center">
                <p className="text-3xl font-bold text-white">{attempt?.path_taken?.length || 0}</p>
                <p className="text-sm text-zinc-400">Steps Completed</p>
              </div>
            </div>

            {/* Feedback */}
            <div className="p-8">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Brain className="h-5 w-5 text-[#1EBFB3]" />
                NAVI's Feedback
              </h3>
              <p className="text-zinc-300 mb-6">{outcome?.feedback}</p>
              
              {/* Key Learning Points */}
              {outcome?.learning_points?.length > 0 && (
                <div className="bg-zinc-900/50 rounded-xl p-6 mb-6">
                  <h4 className="text-sm font-medium text-zinc-400 mb-3">Key Learning Points</h4>
                  <ul className="space-y-2">
                    {outcome.learning_points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-zinc-300">
                        <ChevronRight className="h-4 w-4 text-navigate-gold mt-1 flex-shrink-0" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Development Areas / Learning Gaps */}
              {displayDevelopmentAreas?.length > 0 && (
                <div className="bg-[#1EBFB3]/10 border border-[#1EBFB3]/30 rounded-xl p-6 mb-6">
                  <h4 className="text-sm font-medium text-[#1EBFB3] mb-3 flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Areas for Development
                  </h4>
                  <ul className="space-y-2">
                    {displayDevelopmentAreas.map((area, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-zinc-300">
                        <ChevronRight className="h-4 w-4 text-[#1EBFB3] mt-1 flex-shrink-0" />
                        {area}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {badge ? (
                <LabBadgeEarned badge={badge} token={token} />
              ) : (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 mb-6" data-testid="lab-badge-not-earned">
                  <div className="flex items-center gap-3 mb-3">
                    <Target className="h-6 w-6 text-amber-400" />
                    <h4 className="text-lg font-semibold text-amber-400">Keep Practising</h4>
                  </div>
                  <p className="text-zinc-300 text-sm">
                    You need a score of {passMark}% or higher to pass this lab and earn your Navigate badge. Try the scenario again to improve your score.
                  </p>
                </div>
              )}

              {searchParams.get('programme') && (
                <div className="mb-6"><NextActivityCard programmeId={searchParams.get('programme')} activityId={searchParams.get('activity')} token={token} /></div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-4 pt-6 border-t border-zinc-800">
                <Button
                  onClick={restartLab}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </Button>
                <Button
                  onClick={() => navigate('/labs')}
                  className="bg-navigate-gold text-black hover:bg-navigate-gold-light flex items-center gap-2"
                >
                  Back to Labs
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/labs')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Exit Lab
          </button>
          
          <div className="text-center">
            <h1 className="text-lg font-semibold text-white">{lab.title}</h1>
            <div className="flex items-center justify-center gap-4 text-xs text-zinc-500 mt-1">
              <span>{lab.topic}</span>
              <span>•</span>
              <span className="capitalize">{lab.level}</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Feedback Component */}
            <ContentFeedback
              contentType="learning_lab"
              contentId={labId}
              token={token}
              size="sm"
            />
            
            <div className="text-right text-sm">
              <p className="text-zinc-400">Score</p>
              <p className="text-white font-semibold">{attempt?.total_points || 0} pts</p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between text-xs text-zinc-500 mb-2">
            <span>Progress</span>
            <span>{attempt?.steps_completed?.length || 0} steps completed</span>
          </div>
          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#1EBFB3] to-navigate-gold transition-all duration-500"
              style={{
                width: `${lab.steps.length > 0 
                  ? ((attempt?.steps_completed?.length || 0) / lab.steps.length) * 100 
                  : 0}%`
              }}
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-navigate-surface border border-zinc-800 rounded-2xl overflow-hidden">
          {/* Step Image */}
          {currentStep.image_url && (
            <div className="h-64 bg-zinc-900">
              <img
                src={currentStep.image_url}
                alt={currentStep.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Step Content */}
          <div className="p-8">
            {/* Step Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  currentStep.step_type === 'choice' ? 'bg-blue-500/20' :
                  currentStep.step_type === 'text_response' ? 'bg-[#1EBFB3]/20' :
                  currentStep.step_type === 'checkpoint' ? 'bg-amber-500/20' :
                  'bg-zinc-700'
                }`}>
                  {currentStep.step_type === 'choice' ? (
                    <Target className="h-5 w-5 text-blue-400" />
                  ) : currentStep.step_type === 'text_response' ? (
                    <MessageCircle className="h-5 w-5 text-[#1EBFB3]" />
                  ) : currentStep.step_type === 'checkpoint' ? (
                    <Brain className="h-5 w-5 text-amber-400" />
                  ) : (
                    <Sparkles className="h-5 w-5 text-zinc-400" />
                  )}
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wide">
                    {currentStep.step_type === 'choice' ? 'Decision Point' :
                     currentStep.step_type === 'text_response' ? 'Your Response Required' :
                     currentStep.step_type === 'checkpoint' ? 'NAVI Checkpoint' :
                     'Scenario'}
                  </p>
                  <h2 className="text-xl font-semibold text-white">{currentStep.title}</h2>
                </div>
              </div>
              
              {/* Audio Control */}
              {currentStep.audio_url && (
                <button
                  onClick={toggleAudio}
                  className="p-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors"
                >
                  {audioPlaying ? (
                    <VolumeX className="h-5 w-5 text-zinc-400" />
                  ) : (
                    <Volume2 className="h-5 w-5 text-navigate-gold" />
                  )}
                </button>
              )}
              {currentStep.audio_url && (
                <audio
                  ref={audioRef}
                  src={currentStep.audio_url}
                  onEnded={() => setAudioPlaying(false)}
                />
              )}
            </div>

            {/* Context */}
            <div className="prose prose-invert max-w-none mb-8">
              <p className="text-zinc-300 whitespace-pre-wrap leading-relaxed">
                {currentStep.context}
              </p>
            </div>

            {/* NAVI Feedback Overlay */}
            {showFeedback && naviFeedback && (
              <div className="bg-[#1EBFB3]/10 border border-[#1EBFB3]/30 rounded-xl p-6 mb-6 animate-fade-in">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1EBFB3]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Brain className="h-6 w-6 text-[#1EBFB3]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-[#1EBFB3]">NAVI's Feedback</h4>
                      <span className="text-sm text-zinc-400">
                        +{naviFeedback.points} points
                      </span>
                    </div>
                    <p className="text-zinc-300 mb-4">{naviFeedback.feedback}</p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-zinc-500">
                        Competency: <span className="text-navigate-gold font-medium">{Math.round(naviFeedback.competency)}%</span>
                      </span>
                      <span className="text-zinc-500">
                        Total Score: <span className="text-white font-medium">{naviFeedback.totalPoints} pts</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Interaction Area */}
            {!showFeedback && (
              <>
                {/* Choice Options */}
                {currentStep.step_type === 'choice' && currentStep.choices && (
                  <div className="space-y-3 mb-6">
                    {currentStep.choices.map((choice, idx) => (
                      <button
                        key={choice.choice_id}
                        onClick={() => setSelectedChoice(choice.choice_id)}
                        className={`w-full p-4 rounded-xl border text-left transition-all ${
                          selectedChoice === choice.choice_id
                            ? 'bg-navigate-gold/20 border-navigate-gold text-white'
                            : 'bg-zinc-800/50 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${
                            selectedChoice === choice.choice_id
                              ? 'bg-navigate-gold text-black'
                              : 'bg-zinc-700 text-zinc-400'
                          }`}>
                            {String.fromCharCode(65 + idx)}
                          </span>
                          <p className="pt-1">{choice.text}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Text Response */}
                {currentStep.step_type === 'text_response' && (
                  <div className="mb-6">
                    <Textarea
                      value={textResponse}
                      onChange={(e) => setTextResponse(e.target.value)}
                      placeholder="Type your response here..."
                      className="bg-zinc-900 border-zinc-700 min-h-[150px] text-white"
                    />
                    <p className="text-xs text-zinc-500 mt-2">
                      NAVI will evaluate your response based on professionalism, completeness, and appropriateness.
                    </p>
                  </div>
                )}

                {/* Submit / Continue Buttons */}
                <div className="flex justify-end gap-3">
                  {(currentStep.step_type === 'information' || currentStep.step_type === 'checkpoint') ? (
                    <Button
                      onClick={handleInformationContinue}
                      disabled={submitting}
                      className="bg-navigate-gold text-black hover:bg-navigate-gold-light flex items-center gap-2"
                    >
                      {submitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          Continue
                          <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      onClick={submitResponse}
                      disabled={submitting || (currentStep.step_type === 'choice' && !selectedChoice) || (currentStep.step_type === 'text_response' && !textResponse.trim())}
                      className="bg-navigate-gold text-black hover:bg-navigate-gold-light flex items-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          Submit Response
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </>
            )}

            {/* Continue after feedback */}
            {showFeedback && !isCompleted && (
              <div className="flex justify-end">
                <Button
                  onClick={continueToNext}
                  className="bg-navigate-gold text-black hover:bg-navigate-gold-light flex items-center gap-2"
                >
                  Continue
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
